import { CardMedia, Grid, Typography } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { urlConfig } from '../../../../../config/apiConfig'
import axiosInstance from '../../../../../config/axiosConfig'
import dayjs from 'dayjs'
import SettingsIcon from '@mui/icons-material/Settings'
import CustomModal from '../../../../common/popup/CustomModal'
import ActionButtons from '../../../../common/popup/ActionButtons'
import TextFieldInput from '../../../../common/input/TextFieldInput'
import { CircularProgress } from '@mui/material'

export default function DeviceDetailCard({ data, handleAlert, user }) {
    const [gatewayDetail, setGatewayDetail] = useState(null)
    const [open, setOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [detail, setDetail] = useState()

    const [deviceHistoryGraph, setDeviceHistoryGraph] = useState({
        data: null,
        has: false,
    })

    const [input, setInput] = useState({
        time: '',
        min_x: '',
        max_x: '',
        min_y: '',
        max_y: '',
    })

    const handleGetGatewayById = useCallback(async () => {
        try {
            const res = await axiosInstance.get(
                `${urlConfig.gateway}/${data.gateway_id}`,
            )
            if (res.data.status === 200) {
                setGatewayDetail(res.data.message)
            }
        } catch (error) {
            console.error(error)
        }
    }, [data])

    const getDeviceById = useCallback(async () => {
        try {
            const res = await axiosInstance.get(
                `${urlConfig.deviceById}/${data.device_id}`,
            )
            if (res.data.status === 200) {
                const msg = res.data.message
                setDeviceHistoryGraph({ data: msg, has: true })
                setInput({
                    time: msg.setting_history_log ?? '',
                    min_x: msg.min_x ?? 0,
                    max_x: msg.max_x ?? 0,
                    min_y: msg.min_y ?? 0,
                    max_y: msg.max_y ?? 0,
                })
            } else {
                setDeviceHistoryGraph({ data: null, has: false })
            }
        } catch (error) {
            setDeviceHistoryGraph({ data: null, has: false })
            console.error(error)
        }
    }, [data])

    const handleSubmit = async () => {
        try {
            const body = {
                time: input.time,
            }
            const method = 'put'
            const url = `${urlConfig.deviceHistoryTime}/${data.device_id}`
            const res = await axiosInstance[method](url, body)

            if (res.data.status === 201 || res.data.status === 200) {
                handleAlert('Setting saved successfully.', 'success')
                setOpen(false)
                getDeviceById()
                // getSettingHistoryGraph()
            }
        } catch (error) {
            const err = error.response?.data?.message
            handleAlert(`Failed to save setting. ${err}`, 'error')
        }
    }

    useEffect(() => {
        const getDetail = async () => {
            try {
                const res = await axiosInstance(
                    `${urlConfig.deviceIot}/id/${data.device_id}`,
                )
                setDetail(res.data.message)
            } catch (err) {
                console.error(err)
            }
        }

        if (data && !detail) {
            getDetail()
        }
    }, [data])

    useEffect(() => {
        const fetchAll = async () => {
            try {
                await Promise.all([
                    handleGetGatewayById(),
                    getDeviceById(),
                    // getSettingHistoryGraph(),
                ])
            } catch (err) {
                console.error('Error loading data:', err)
            } finally {
                setIsLoading(false)
            }
        }

        fetchAll()
    }, [handleGetGatewayById, getDeviceById])

    if (isLoading) {
        return (
            <Grid
                container
                justifyContent='center'
                alignItems='center'
                minHeight='300px'
            >
                <CircularProgress />
            </Grid>
        )
    }

    return (
        <>
            <Grid container sx={{ position: 'relative' }}>
                {!user && (
                    <SettingsIcon
                        onClick={() => !isLoading && setOpen(true)}
                        sx={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            cursor: isLoading ? 'not-allowed' : 'pointer',
                            opacity: isLoading ? 0.5 : 1,
                        }}
                    />
                )}
                <Typography variant='h6' textAlign={'left'} mb={1}>
                    Device detail
                </Typography>
                <Grid container gap={1}>
                    <Grid item xs={12} md={4} sm={5}>
                        <CardMedia
                            component='img'
                            image={
                                data.image
                                    ? `${urlConfig.baseUrlAPI}/api/v1${data.image}`
                                    : 'https://t4.ftcdn.net/jpg/04/73/25/49/360_F_473254957_bxG9yf4ly7OBO5I0O5KABlN930GwaMQz.jpg'
                            }
                            width={'100%'}
                            sx={{ borderRadius: 2 }}
                            alt='Device Image'
                        />
                    </Grid>
                    <Grid item flex={1} xs={12} sm={6} md={7}>
                        <Typography variant='body2' fontWeight={'bold'}>
                            Device Type: <span>{data.device_type}</span>
                        </Typography>
                        <Typography variant='body2' fontWeight={'bold'}>
                            ID Name: <span>{data.device_id}</span>
                        </Typography>
                        <Typography variant='body2' fontWeight={'bold'}>
                            S/N: <span>{data?.s_n}</span>
                        </Typography>
                        <Typography variant='body2' fontWeight={'bold'}>
                            Location: <span>{detail?.description}</span>
                        </Typography>
                        <Typography variant='body2' fontWeight={'bold'}>
                            Gateway: <span>{gatewayDetail?.name}</span>
                        </Typography>
                        <Typography variant='body2' fontWeight={'bold'}>
                            Last update:{' '}
                            <span>
                                {dayjs(data.updatedAt).format(
                                    'DD/MM/YYYY, HH:mm:ss',
                                )}
                            </span>
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>
            <CustomModal
                size='sm'
                open={open && !isLoading}
                setOpen={setOpen}
                handleClose={() => {
                    setOpen(false)
                    // getSettingHistoryGraph()
                    getDeviceById()
                }}
            >
                <Grid container spacing={1} p={1}>
                    <Grid item xs={12}>
                        <Typography variant='h5' gutterBottom>
                            {deviceHistoryGraph.has ? 'Edit' : 'Setting'} Device
                        </Typography>

                        <TextFieldInput
                            state={input}
                            setState={setInput}
                            name='time'
                            label='History Log Interval (seconds)'
                            size='small'
                            isNumber={true}
                            fullWidth
                        />
                    </Grid>

                    {/* <Grid item xs={12} mt={2}>
                        <Typography variant='h5' gutterBottom>
                            Graph Trend Setting
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextFieldInput
                                    state={input}
                                    setState={setInput}
                                    name='min_y'
                                    label='Minimum Y'
                                    size='small'
                                    isNumber={true}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextFieldInput
                                    state={input}
                                    setState={setInput}
                                    name='max_y'
                                    label='Maximum Y'
                                    size='small'
                                    isNumber={true}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                    </Grid> */}

                    <Grid item xs={12}>
                        <ActionButtons
                            canSave={
                                Number(input.time) > 0 &&
                                input.min_x !== undefined &&
                                input.max_x !== undefined &&
                                input.min_y !== undefined &&
                                input.max_y !== undefined
                            }
                            onSave={handleSubmit}
                            onCancel={() => {
                                setOpen(false)
                                // getSettingHistoryGraph()
                                getDeviceById()
                            }}
                        />
                    </Grid>
                </Grid>
            </CustomModal>
        </>
    )
}
